import {IDataModelTypeConfig} from "frontend/dataModels/types/IDataModelTypeConfig";
import {markRaw} from 'vue'
import TrueFalseLabel from 'frontend/common/true-false-label.vue'
import ShowNumberOfHours from 'frontend/common/show-number-of-hours.vue'
import ShiftCheckInOut from 'frontend/driver-sources/driver-source-shift-check-in-out.vue'
import InclusiveShiftCheckInOut from 'frontend/driver-sources/driver-source-inclusive-shift-check-in-out.vue'
import FormattedTime from 'frontend/common/formatted-time.vue'

export const DriverSourceDataModelTypeConfig: IDataModelTypeConfig = {
  fields: [
    'id',
    'eid',
    'name',
    'billable',
    'default_shift_length',
    'max_overtime',
    'min_time_between_shifts',
    'max_shift_length',
    'min_staging_time',
    'max_cont_driving_time',
    'default_break_time',
    'total_break_time',
    'min_break_time',
    'max_break_time',
    'shift_check_in_time',
    'shift_check_out_time',
    'inclusive_shift_check_in_out',
    'updated_at',
    'created_at',
  ],
  subscriptions: {
    items: 'DriverSourcesChannel',
  },
  defaultColumns: [
    'eid',
    'name',
    'billable',
    'defaultShiftLength',
    'maxOvertime',
    'minTimeBetweenShifts',
    'maxShiftLength',
    'minStagingTime',
    'maxContDrivingTime',
    'defaultBreakTime',
    'totalBreakTime',
    'minBreakTime',
    'maxBreakTime',
    'shiftCheckInTime',
    'shiftCheckOutTime',
    'inclusiveShiftCheckInOut',
    'updatedDatetime',
  ],
  columnConfig: {
    eid: {
      label: 'EID',
      header: {
        sort: 'eid',
      },
      cell: {
        kind: 'simple',
        path: 'eid',
      },
    },
    name: {
      label: 'Name',
      header: {
        sort: 'lowerName',
      },
      cell: {
        kind: 'simple',
        path: 'name',
      },
    },
    billable: {
      label: 'Billable',
      header: {
        sort: 'billable',
      },
      cell: {
        kind: 'component',
        component: markRaw(TrueFalseLabel),
        componentProps: driverSource => ({ item: driverSource.billable }),
      },
    },
    defaultShiftLength: {
      label: 'Default shift length',
      header: {
        sort: 'defaultShiftLength',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.defaultShiftLength }),
      },
    },
    maxOvertime: {
      label: 'Max overtime',
      header: {
        sort: 'maxOvertime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.maxOvertime }),
      },
    },
    minTimeBetweenShifts: {
      label: 'Min time between shifts',
      header: {
        sort: 'minTimeBetweenShifts',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.minTimeBetweenShifts }),
      },
    },
    maxShiftLength: {
      label: 'Max shift length',
      header: {
        sort: 'maxShiftLength',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.maxShiftLength }),
      },
    },
    minStagingTime: {
      label: 'Min staging time',
      header: {
        sort: 'minStagingTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.minStagingTime }),
      },
    },
    maxContDrivingTime: {
      label: 'Max continuous driving time',
      header: {
        sort: 'maxContDrivingTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.maxContDrivingTime }),
      },
    },
    defaultBreakTime: {
      label: 'Default break time',
      header: {
        sort: 'defaultBreakTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.defaultBreakTime }),
      },
    },
    totalBreakTime: {
      label: 'Total break time',
      header: {
        sort: 'totalBreakTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.totalBreakTime }),
      },
    },
    minBreakTime: {
      label: 'Min break time',
      header: {
        sort: 'minBreakTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.minBreakTime }),
      },
    },
    maxBreakTime: {
      label: 'Max break time',
      header: {
        sort: 'maxBreakTime',
      },
      cell: {
        kind: 'component',
        component: markRaw(ShowNumberOfHours),
        componentProps: driverSource => ({ numberOfHours: driverSource.maxBreakTime }),
      },
    },
    shiftCheckInTime: {
      label: 'Shift check-in time',
      header: {
        sort: 'shiftCheckInTime',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: markRaw(ShiftCheckInOut),
        componentProps: driverSource => ({
          driverSource,
          attr: 'shiftCheckInTime',
        }),
      },
    },
    shiftCheckOutTime: {
      label: 'Shift check-out time',
      header: {
        sort: 'shiftCheckOutTime',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: markRaw(ShiftCheckInOut),
        componentProps: driverSource => ({
          driverSource,
          attr: 'shiftCheckOutTime',
        }),
      },
    },
    inclusiveShiftCheckInOut: {
      label: 'Shift CI/CO included',
      tooltip: 'Shift check-in/check-out times are included in the shift length',
      header: {
        sort: 'inclusiveShiftCheckInOut',
      },
      cell: {
        kind: 'component',
        component: markRaw(InclusiveShiftCheckInOut),
        componentProps: driverSource => ({
          driverSource,
        }),
      },
    },
    updatedDatetime: {
      label: 'Updated Datetime',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.updatedAt,
        }),
      },
    },
    updatedDate: {
      label: 'Updated Date',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.updatedAt,
          format: 'dateOnly',
        }),
      },
    },
    updatedTime: {
      label: 'Updated Time',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.updatedAt,
          format: 'timeOnly',
        }),
      },
    },
    createdDatetime: {
      label: 'Created Datetime',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.createdAt,
        }),
      },
    },
    createdDate: {
      label: 'Created Date',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.createdAt,
          format: 'dateOnly',
        }),
      },
    },
    createdTime: {
      label: 'Created Time',
      header: {
        sort: 'updatedAt',
      },
      cell: {
        kind: 'component',
        component: markRaw(FormattedTime),
        componentProps: driverSource => ({
          t: driverSource.createdAt,
          format: 'timeOnly',
        }),
      },
    },
  }
}